<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          <span v-if="selectedParticipantId != null">
            {{
              noDataExist
                ? "Customer"
                : beautifyName(originalFirstName, originalLastName)
            }}
          </span>
          <span v-else-if="!loading">
            {{ $label("customer.new_participant", "New participant") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs :customer-id="selectedParticipantId"></navigation-tabs>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" class="row">
        <div class="col-sm-12">
          <h2>{{ $label("customer.participant", "Participant") }}</h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Gender -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.gender", "Gender")
                    }}</label>
                    <label class="select">
                      <select
                        v-model="selectedParticipant.gender"
                        :disabled="updating"
                        :class="{ 'text-input-error': hasError('gender') }"
                        id="participant-gender"
                      >
                        <option :value="0">{{
                          $label("common.gender", "Gender")
                        }}</option>
                        <option :value="1">{{
                          $label("common.gender_female", "Female")
                        }}</option>
                        <option :value="2">{{
                          $label("common.gender_male", "Male")
                        }}</option>
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('gender')" class="text-danger">
                      {{ getError("gender") }}
                    </p>
                  </section>
                  <!-- End Gender -->

                  <!-- BEGIN Birthdate -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.date_of_birth", "Date of birth")
                    }}</label>
                    <date-picker
                      v-model="selectedParticipant.birthdate"
                      :format="dateFormat"
                      :readonly="updating"
                      :disabled="updating"
                      class="input"
                      id="participant-birthdate"
                      :placeholder="dateFormat"
                      :class="{ 'text-input-error': hasError('birthdate') }"
                    >
                    </date-picker>

                    <p v-if="hasError('birthdate')" class="text-danger">
                      {{ getError("birthdate") }}
                    </p>
                  </section>
                  <!-- END Birthdate -->
                </div>
                <div class="row">
                  <!-- BEGIN Last name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedParticipant.last_name"
                      id="participant-last-name"
                      :label="$label('common.last_name', 'Last name')"
                      :placeholder="$label('common.last_name', 'Last name')"
                      :has-error="hasError('last_name')"
                      :error="getError('last_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateParticipant"
                    />
                  </section>
                  <!-- END Last name -->

                  <!-- BEGIN First name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedParticipant.first_name"
                      id="participant-first-name"
                      :label="$label('common.first_name', 'First name')"
                      :placeholder="$label('common.first_name', 'First name')"
                      :has-error="hasError('first_name')"
                      :error="getError('first_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateParticipant"
                    />
                  </section>
                  <!-- END First name -->
                </div>

                <div class="row">
                  <!-- BEGIN Email -->
                  <section class="col col-12">
                    <label class="label">{{
                      $label("common.email_address_long", "E-Mail address")
                    }}</label>
                    <label class="input">
                      <i class="icon-append icon-envelope"></i>
                      <input
                        v-model="selectedParticipant.email"
                        :class="{ 'text-input-error': hasError('email') }"
                        :placeholder="$label('common.email_address_short')"
                        :disabled="updating"
                        id="participant-email"
                        type="email"
                      />
                    </label>

                    <p v-if="hasError('email')" class="text-danger">
                      {{ getError("email") }}
                    </p>
                  </section>
                  <!-- END Email -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
      <hr />

      <div class="row">
        <div class="col-xs-3 text-center">&nbsp;</div>

        <div class="col-xs-6 text-center">
          <back-button
            back-link="CustomerParticipants"
            :class="{ 'is-disabled': updating }"
            id="back-participant-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-participant-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateParticipant"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedParticipantId == null
            }"
            id="delete-participant-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteParticipant"
          >
          </danger-button>
        </div>

        <div class="col-xs-3 text-center">&nbsp;</div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import swal from "sweetalert";
import Api from "@/api";

import FormErrorsMixin from "@/mixins/FormErrors";
import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import NavigationTabs from "@/components/Customers/Customer/NavigationTabs";
import Error404 from "@/components/Common/Error404";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  name: "CustomerParticipantDetails",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    NavigationTabs,
    Error404,
    DatePicker
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      originalFirstName: null,
      originalLastName: null,
      dateFormat: "DD.MM.YYYY",
      selectedParticipant: {
        gender: null,
        title: null,
        first_name: null,
        last_name: null,
        email: null,
        birthdate: null
      },
      selectedCustomerId: null,
      selectedParticipantId: null,
      failedRequest: false,
      validatedInputs: [
        "customer_id",
        "gender",
        "first_name",
        "last_name",
        "email",
        "birthdate"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedCustomerId = vm.$route.params.customer_id;
      vm.selectedParticipantId = vm.$route.params.participant_id;
      if (
        typeof vm.selectedCustomerId !== "undefined" &&
        vm.selectedCustomerId !== null &&
        typeof vm.selectedParticipantId !== "undefined" &&
        vm.selectedParticipantId !== null
      ) {
        if (vm.$route.query.participant_created) {
          swal("Success!", "The participant was created.", "success").then(
            () => {
              window.history.pushState(
                {},
                document.title,
                window.location.pathname
              );
            }
          );
        }
        vm.getParticipant(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (
      typeof this.selectedCustomerId !== "undefined" &&
      this.selectedCustomerId !== null &&
      typeof this.selectedParticipantId !== "undefined" &&
      this.selectedParticipantId !== null
    ) {
      this.getParticipant(() => {
        //
      });
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateParticipant() {
      if (this.selectedParticipantId == null) {
        this.createParticipant();
      } else {
        this.updateParticipant();
      }
    },

    createParticipant() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("participants");

      Api()
        .post(endpoint, {
          ...this.selectedParticipant,
          birthdate: this.convertDate(this.selectedParticipant.birthdate),
          customer_id: this.selectedCustomerId
        })
        .then(response => {
          this.selectedParticipantId = response.data.data.id;
          this.$router.push({
            name: "CustomerParticipantDetails",
            params: {
              customer_id: this.selectedCustomerId,
              participant_id: this.selectedParticipantId
            },
            query: { participant_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#participant-last-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getParticipant() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "participants/" + this.selectedParticipantId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedParticipant = response.data.data;
          this.selectedParticipant.birthdate =
            response.data.data.birthdate !== null
              ? new Date(response.data.data.birthdate)
              : new Date();
          this.originalFirstName = this.selectedParticipant.first_name;
          this.originalLastName = this.selectedParticipant.last_name;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateParticipant() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "participants/" + this.selectedParticipantId
      );

      Api()
        .put(endpoint, {
          ...this.selectedParticipant,
          birthdate: this.convertDate(this.selectedParticipant.birthdate),
          customer_id: this.selectedCustomerId
        })
        .then(() => {
          this.getParticipant(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The participant was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#participant-last-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteParticipant() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The participant will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "participants/" + this.selectedParticipantId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "CustomerParticipants",
                query: { participant_deleted: true }
              });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete participants.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}
</style>
